import type { VFC } from 'react';
import React from 'react';

import type { AnnotationProviders } from '@atlaskit/editor-common/types';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { useEditorContentModeValue } from '@confluence/editor-content-mode-button/entry-points/useEditorContentModeValue';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess';
import { MustPreloadLoadableNames, mustPreloadWhenRenderingSPA } from '@confluence/loadable';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes';
import { RendererExtensionHandlers } from '@confluence/fabric-extension-lib/entry-points/extensionTypes';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { convertRendererProps, getMacroId } from './utils';

export type RendererExtensionHandlerProps = ExtensionHandlerProps & {
	appearance?: string;

	// WARN: These 3 attributes below added to allow for seamless transition from server-side rendering
	// where ADFRenderer wrapper is used, to client-side rendering where classic (read: legacy) Renderer
	// is used. In the future, legacy Renderer has to be replaced with ADFRenderer to keep things uniform.
	Renderer: any;
	isLegacyRenderer: boolean;
	getExtensionHandlers?: (options: any) => RendererExtensionHandlers;

	mediaToken?: string;
	annotationProvider?: AnnotationProviders;
};

export const ExcerptExtensionHandlerComponent: VFC<RendererExtensionHandlerProps> = ({
	contentId,
	extensionKey,
	mode,
	node,
	spaceKey,
	appearance,
	showTemplateVariablesInPreview,
	showTemplateInputInPreview,
	Renderer,
	isLegacyRenderer,
	getExtensionHandlers,
	mediaToken,
	annotationProvider,
}) => {
	mustPreloadWhenRenderingSPA([MustPreloadLoadableNames.ClassicRendererLoader]);

	const renderServerPlaceholder = useRenderServerPlaceholder();
	const hiddenMacroParamValue = node?.parameters?.macroParams?.hidden?.value;
	const isHidden = hiddenMacroParamValue === 'false' ? false : Boolean(hiddenMacroParamValue);
	const attributes = getMacroAttributesFromADFNode(node);
	const document = { type: 'doc', version: 1, content: node?.content };

	const { mode: contentMode } = useEditorContentModeValue({ contentId });
	const isCompactModeSupported =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		expValEquals('cc_editor_ai_content_mode', 'variant', 'test') &&
		fg('platform_editor_content_mode_button_mvp');

	// ADFRenderer (next-based) requires extension handlers during rendering on server side,
	// for extensions like this as they use renderer internally. Handlers aren't needed with
	// legacy renderer because legacy component cooks them internally.
	const extensionHandlers =
		getExtensionHandlers && !isLegacyRenderer
			? getExtensionHandlers({
					adf: document,
					contentId,
					appearance,
					showTemplateInputInPreview,
					showTemplateVariablesInPreview,
				})
			: undefined;

	const macroId = getMacroId(node);
	return (
		<div
			data-fabric-macro={macroId}
			data-vc="excerpt-macro"
			{...(renderServerPlaceholder
				? { 'data-ssr-placeholder': macroId }
				: { 'data-ssr-placeholder-replace': macroId })}
		>
			{isHidden ? null : (
				<Renderer
					{...convertRendererProps(isLegacyRenderer, { contentId, document })}
					spaceKey={spaceKey}
					appearance={appearance || 'default'}
					contentMode={isCompactModeSupported ? contentMode : undefined}
					allowStickyHeaders={false}
					extensionHandlers={extensionHandlers}
					showTemplateInputInPreview={showTemplateInputInPreview}
					showTemplateVariablesInPreview={showTemplateVariablesInPreview}
					mediaToken={mediaToken}
					annotationProvider={annotationProvider}
					allowAnnotations={!!annotationProvider}
					{...(editorExperiment('comment_on_bodied_extensions', true)
						? { allowInlineComments: true }
						: {})}
				/>
			)}
			<MacroExperienceSuccess
				name={getExperienceName(mode, node)}
				extensionKey={extensionKey}
				mode={mode}
				attributes={attributes}
				contentId={contentId}
			/>
		</div>
	);
};

export const ExcerptExtensionHandler = (props: RendererExtensionHandlerProps) => (
	<ErrorBoundary attribution={Attribution.PAGE_EXTENSIONS}>
		<ExcerptExtensionHandlerComponent {...props} />
	</ErrorBoundary>
);
